/**
 * Type definitions for saas-admin. Mirrors the DTOs in
 * docs/background/saas_admin_frontend_reference.md §6 + saas-auth Java DTOs.
 *
 * Envelope:
 *   { success: true, message: "...", data: T }   (success)
 *   { success: false, error: { code, message } } (failure, handled by ApiError)
 */
// SASADM-0148 — type-only, so this stays a leaf module (no runtime import cycle
// with `./admin/hostnames`, which imports value bindings from here).
import type { DnsCheckResult } from './admin/hostnames';

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/** SASATH-0387 §5.1 — the outcome of the routing deploy a write triggered. Absent = this write didn't deploy. */
export type RoutingDeployStatus =
  | 'DEPLOYED'
  | 'PARTIAL'
  | 'QUEUED'
  | 'SKIPPED'
  | 'REJECTED'
  | 'FAILED';
/** SASATH-0391 — a tenant or product kept on its previous routing because it failed to render. */
export interface RoutingHeldBack {
  kind: 'TENANT' | 'PRODUCT';
  id: number;
  slug: string;
}
export interface RoutingDeployOutcome {
  status: RoutingDeployStatus;
  /** Present for PARTIAL, REJECTED, FAILED and SKIPPED. */
  message?: string;
  /** SASATH-0391 — PARTIAL only. */
  heldBack?: RoutingHeldBack[];
}
/** Mixed into every response DTO that may carry the outcome (inside `data`, SASATH-0387 §3.1). */
export interface WithRoutingDeploy {
  routingDeploy?: RoutingDeployOutcome;
}

/** SASATH-0387 §3 — one front-end app's build folder for one tenant. */
export interface AppOverrideDto extends WithRoutingDeploy {
  appId: number;
  appSlug: string;
  appName: string;
  role: string;
  defaultDocroot: string;
  overrideDocroot: string | null;
  /** SASATH-0387 §3A — false = this tenant's public app is turned off. */
  enabled: boolean;
  /** SASATH-0389 §3.2 — the folder served while the app is off; null = the plain "unavailable" page. */
  offlineDocroot: string | null;
  updatedAt: string | null;
}

/**
 * SASATH-0212 — the paged `data` envelope. The collection reads that page
 * (`GET /users`, `GET /tenants`) return this in place of a bare array; the row
 * DTOs themselves are unchanged. `page` is 0-based.
 */
export interface PagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * SASATH-0212 paging/search params. `size` defaults to 50 server-side and is
 * clamped to 200. `q` is a case-insensitive contains match (email+name for
 * users, slug+name for tenants); `%`, `_` and `\` are matched LITERALLY by the
 * server, so callers must not escape them.
 */
export interface PageParams {
  page?: number;
  size?: number;
  q?: string;
}

// ---------------------------------------------------------------------------
// Enums (as TS unions)
// ---------------------------------------------------------------------------

export const TENANT_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const TENANT_SIGNUP_MODES = [
  'PUBLIC',
  'INVITE_ONLY',
  'ADMIN_CREATED_ONLY',
  'DISABLED',
] as const;
export type TenantSignupMode = (typeof TENANT_SIGNUP_MODES)[number];

export const TENANT_USER_ROLES = ['OWNER', 'SUPER_ADMIN', 'ADMIN', 'USER'] as const;
export type TenantUserRole = (typeof TENANT_USER_ROLES)[number] | 'STAFF' | 'GUEST';

/** SASATH-0376 — which membership table a member row comes from. */
export type MemberKind = 'CUSTOMER' | 'STAFF';
/** Staff (admin-app) roles, highest first. A customer's role is not editable. */
export const STAFF_ROLES = ['OWNER', 'ADMIN', 'STAFF'] as const satisfies readonly TenantUserRole[];
/** SASADM-0169 — invite/add role choices, grouped: employee app, then admin app. */
export const EMPLOYEE_ROLE = 'USER' as const;

export const TENANT_USER_STATUSES = ['ACTIVE', 'INVITED', 'DISABLED'] as const;
export type TenantUserStatus = (typeof TENANT_USER_STATUSES)[number];

export const HOSTNAME_TYPES = ['APP', 'LOGIN', 'AUTH', 'API', 'ASSET', 'BRAND'] as const;
export type HostnameType = (typeof HOSTNAME_TYPES)[number];

export const HOSTNAME_SOURCES = ['SYSTEM', 'OPERATOR'] as const;
export type HostnameSource = (typeof HOSTNAME_SOURCES)[number];

export const EMAIL_TEMPLATE_NAMES = [
  'EMAIL_VERIFICATION',
  'PASSWORD_RESET',
  'GOOGLE_SIGNIN_NOTICE',
  'MFA_CODE',
  'TENANT_INVITATION',
] as const;
export type EmailTemplateName = (typeof EMAIL_TEMPLATE_NAMES)[number];

export const MFA_POLICIES = ['REQUIRED', 'OPTIONAL', 'DISABLED', 'ALWAYS'] as const;
export type MfaPolicy = (typeof MFA_POLICIES)[number];

export const INVITATION_STATUSES = ['PENDING', 'ACCEPTED', 'REVOKED', 'EXPIRED'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const PLATFORM_ADMIN_ROLES = ['SAAS_ADMIN', 'SUPER_ADMIN'] as const;
export type PlatformAdminRole = (typeof PLATFORM_ADMIN_ROLES)[number];

// Platform operators (SASATH-0116 `/api/saas-admin/operators`). An operator is a
// tenant-less, claim-based platform principal (`platform_admins`). `OperatorDto`
// is kept conservative — reconcile field names against the SASATH-0116 delivery
// once it merges (see the SASADM-0077 delivery's Architect notes).
export interface OperatorDto {
  userId: number;
  email: string;
  name?: string | null;
  role: PlatformAdminRole;
  grantedAt: string;
  // SASATH-0213 — batch-resolved email of the granting operator; null when that
  // user was deleted. Optional until 0213 deploys. Carried for contract
  // completeness only: the operators panel has no "Granted by" column.
  grantedByEmail?: string | null;
}

export interface GrantOperatorRequest {
  userId?: number;
  email?: string;
  role: PlatformAdminRole;
}

// Product admins (SASADM-0105; saas-auth SASATH-0211
// `/api/saas-admin/products/{productId}/admins`). A product admin is an operator
// appointed to administer one product's tenants (`product_admins`). Grants are
// append-only and revokes are soft, so a row carries `revokedAt` and the list
// endpoint takes `includeRevoked`. One role exists today.
export const PRODUCT_ADMIN_ROLES = ['PRODUCT_ADMIN'] as const;
export type ProductAdminRole = (typeof PRODUCT_ADMIN_ROLES)[number];

export interface ProductAdminDto {
  id: number;
  userId: number;
  email: string;
  role: ProductAdminRole;
  productId: number;
  grantedBy: number | null;
  // SASATH-0213 — batch-resolved email of the granting operator, alongside the
  // raw `grantedBy` id; null when that user was deleted. Optional until 0213
  // deploys, so the "Granted by" column falls back to the id.
  grantedByEmail?: string | null;
  grantedAt: string;
  revokedAt: string | null;
}

export interface GrantProductAdminRequest {
  userId?: number;
  email?: string;
  role: ProductAdminRole;
}

// ---------------------------------------------------------------------------
// Auth DTOs
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: number;
  name?: string;
  email: string;
  emailVerified?: boolean;
  googleLinked?: boolean;
  locale?: string;
}

export interface AuthTokenResponse {
  accessToken: string;
  tokenType?: string;
  expiresAt?: string;
  user?: AuthUser;
  /**
   * Double-submit CSRF token to echo as `X-CSRF-Token` on subsequent
   * state-mutating calls (including `/api/saas-admin/**` writes). Captured
   * from the refresh response body — at `saas-admin.journix.ai` the
   * `journix_csrf` cookie is cross-origin and unreadable from JS, so the
   * body is the authoritative source. See specs/0003.
   */
  csrfToken?: string | null;
}

/**
 * Authoritative operator identity from `GET /api/auth/me` for a PLATFORM
 * principal (SASADM-0081 U3 / SASATH-0133). Refines the claims-derived
 * identity. `principalType` is always `'PLATFORM'` on this surface;
 * `platformAdminRole` reuses the existing PlatformAdminRole union.
 *
 * CONTRACT STATUS — built to the documented SASATH-0133 contract; `/me` returns
 * `401` for a platform principal until 0133 deploys (U3 fails soft — see the
 * delivery's Architect notes for the field-name reconciliation to do once live).
 */
export interface OperatorMeResponse {
  userId: number;
  email: string;
  name?: string | null;
  principalType: 'PLATFORM';
  platformAdminRole: PlatformAdminRole; // 'SAAS_ADMIN' | 'SUPER_ADMIN'
}

// ---------------------------------------------------------------------------
// Tenant
// ---------------------------------------------------------------------------

export interface TenantDto {
  id: number;
  slug: string;
  name: string;
  status: TenantStatus;
  signupMode: TenantSignupMode;
  productId: number | null;
  brandId: number | null;
  // Spec 0050 (U4) added an assumed `planId?` here for the tenant's current plan;
  // SASADM-0051 replaced it with the dedicated `tenantPlan.get(tenantId)` read
  // (`TenantPlanAssignment`), so the field is removed — the tenant DTO no longer
  // carries the assigned plan.
  emailTemplatesDir?: string | null;
  emailFromName?: string | null;
  emailFromAddress?: string | null;
  emailSupportEmail?: string | null;
  createdAt: string;
}

/**
 * SASATH-0212 — the `GET /tenants` filters. All optional, independent, and
 * AND-combined with each other and with `q`.
 */
export interface TenantPageParams extends PageParams {
  productId?: number;
  status?: TenantStatus;
  brandId?: number;
  productGroup?: string;
  includeGroupCounts?: boolean;
  /**
   * SASATH-0212 — `<field>,<dir>`, ordering the WHOLE result set (so it works
   * under paging). `field` is allowlisted server-side to
   * `name | slug | createdAt | status` and `dir` to `asc | desc`; anything else
   * is a 400 `INVALID_SORT`. Omitted means the default slug-asc order.
   */
  sort?: string;
}

/** One product-group bucket of the `GET /tenants` facet. */
export interface ProductGroupCount {
  productGroup: string;
  count: number;
}

/**
 * `GET /tenants` page. Carries the product-group facet ONLY when the request
 * asked for it (`includeGroupCounts=true`); the field is omitted otherwise.
 */
export interface TenantPageResponse extends PagedResponse<TenantDto> {
  productGroupCounts?: ProductGroupCount[];
}

export interface CreateTenantRequest {
  slug: string;
  name: string;
  signupMode: TenantSignupMode;
  productId?: number | null;
}

export interface UpdateTenantRequest {
  name?: string;
  signupMode?: TenantSignupMode;
  emailTemplatesDir?: string | null;
  emailFromName?: string | null;
  emailFromAddress?: string | null;
  emailSupportEmail?: string | null;
}

export interface SupportSessionRequest {
  appId: number;
  reason?: string;
}

export interface SupportSessionResponse {
  landingUrl: string;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Hostname
// ---------------------------------------------------------------------------

export interface HostnameDto extends WithRoutingDeploy {
  id: number;
  tenantId: number;
  hostname: string;
  hostnameType: HostnameType;
  // Spec 0028 — one row per (tenant, type) is primary (canonical host). The first
  // host of a type is auto-primary; later rows are isPrimary:false.
  isPrimary: boolean;
  // Spec 0029 — DNS verification is persisted + load-bearing: an unverified host is
  // inactive (no login/cert/CORS, cannot be made primary). Verified is sticky.
  dnsVerified: boolean;
  dnsVerifiedAt: string | null;
  // Spec 0032 — APP hostnames route to one app of the tenant's product. Null for
  // non-APP rows and for legacy/unassigned APP rows (transitional default routing).
  appId?: number | null;
  appSlug?: string | null;
  // SASADM-0146 (saas-auth SASATH-0309) — display name of the assigned app, resolved server-side.
  // Third sibling of `appId`/`appSlug`, and **null exactly when `appSlug` is null** — the two never
  // disagree about whether the app resolved. Exists so a hostname row can be labelled
  // "App: Dink Public App" without a second call for the product's app list.
  appName?: string | null;
  // SASATH-0185/0186/0190 — SYSTEM is an auto-provisioned default; OPERATOR is a
  // manually added custom domain. Optional until the backend contract ships.
  source?: HostnameSource | null;
  // SASATH-0213 — true only for the tenant apex FQDN (`<slug>.<base>`, no app-slug
  // label). Optional until 0213 ships; absent means "no apex row known".
  isApex?: boolean;
  // SASADM-0173 (SASATH-0390 §4) — an APP row may 308-redirect to another live APP row of the
  // tenant (no chains). Present on every read; null = not a redirect. A redirecting row is served
  // (certificate) but not live (no CORS, sign-in or by-hostname).
  redirectToHostnameId?: number | null;
  redirectToHostname?: string | null;
  // SASADM-0178 (SASATH-0395 §3) — an apex re-pointed to a non-primary app is pinned: set-primary
  // leaves it alone. Always serialized once 0395 is deployed.
  appPinned?: boolean;
  createdAt: string;
}

export interface CreateHostnameRequest {
  hostname: string;
  hostnameType: HostnameType;
  // Spec 0032 — optional app assignment (APP-type rows only). Omit to leave unassigned.
  appId?: number | null;
}

// ---------------------------------------------------------------------------
// White-label domain (SASADM-0165, saas-auth SASATH-0297)
// ---------------------------------------------------------------------------

/** SASADM-0165 (saas-auth SASATH-0297) — host types a white-label domain may carry (no API). */
export type WhitelabelHostnameType = 'APP' | 'LOGIN' | 'AUTH' | 'ASSET' | 'BRAND';
export type WhitelabelDomainState = 'DRAFT' | 'ACTIVE';

export interface WhitelabelHostnameDto {
  id: number;
  prefix: string;
  hostname: string;
  hostnameType: WhitelabelHostnameType;
  appId: number | null;
  appSlug: string | null;
  dnsVerified: boolean;
  dnsVerifiedAt: string | null;
}

export interface WhitelabelDnsRecordDto {
  host: string;
  type: 'CNAME';
  target: string;
  dnsVerified: boolean;
}

export interface WhitelabelDomainDto extends WithRoutingDeploy {
  tenantId: number;
  configured: boolean;
  domain: string | null;
  state: WhitelabelDomainState | null;
  activatedAt: string | null;
  hostnames: WhitelabelHostnameDto[];
  dnsConfigured: boolean;
  /** Omitted (or null) when `dnsConfigured` is false. */
  dnsRecords?: WhitelabelDnsRecordDto[] | null;
}

export interface PutWhitelabelDomainRequest {
  domain: string;
  hosts: { prefix: string; hostnameType: WhitelabelHostnameType; appId?: number }[];
}

export interface WhitelabelVerifyResultDto {
  prefix: string;
  hostname: string;
  hostnameType: WhitelabelHostnameType;
  matched: boolean;
  dnsVerified: boolean;
}

export interface WhitelabelVerifyResponse extends WithRoutingDeploy {
  tenantId: number;
  domain: string | null;
  state: WhitelabelDomainState;
  activatedAt: string | null;
  allVerified: boolean;
  results: WhitelabelVerifyResultDto[];
  dnsConfigured: boolean;
  dnsRecords?: WhitelabelDnsRecordDto[] | null;
  /** SASATH-0382 — saas-auth promoted the domain and deployed routing itself on this verify. */
  promotedThisRun?: boolean;
}

// ---------------------------------------------------------------------------
// Product Hostname (SASADM-0148, saas-auth SASATH-0216 + SASATH-0330)
// ---------------------------------------------------------------------------

/**
 * A product's derived console hostname — `admin.<slug>.<platform-base>` (pre-verified,
 * the platform wildcard) and, when the product's `base_domain` is set,
 * `admin.<slug>.<baseDomain>` (APP) + `auth.<slug>.<baseDomain>` (AUTH), both minted
 * unverified until Check DNS. Derived only: no add/delete/primary from this client.
 * SASADM-0152 (saas-auth SASATH-0336) — the RESELLER_ADMIN app adds `reseller.` (APP),
 * `login.` (LOGIN) and `auth.` (AUTH) rows on the platform base, plus `reseller.` / `login.`
 * on the product base domain; none of them is primary. Filter by `appId` per console.
 */
export interface ProductHostnameDto {
  id: number;
  productId: number;
  hostname: string;
  hostnameType: 'APP' | 'AUTH' | 'LOGIN';
  isPrimary: boolean;
  dnsVerified: boolean;
  dnsVerifiedAt?: string | null;
  appId: number;
  appSlug?: string | null;
  createdAt: string;
}

/**
 * `POST .../hostnames/{id}/verify-dns` response (mirrors `VerifyDnsResult` in
 * `./admin/hostnames`) — 200 even on a mismatch; read `check.match`.
 */
export interface ProductVerifyDnsResponse {
  check: DnsCheckResult;
  hostname: ProductHostnameDto;
}

// ---------------------------------------------------------------------------
// Member (TenantUser)
// ---------------------------------------------------------------------------

export interface TenantMemberDto {
  membershipId: number;
  userId: number;
  name?: string;
  email: string;
  imageUrl?: string | null;
  role: TenantUserRole;
  status: TenantUserStatus;
  createdAt: string;
  /** SASATH-0376 — the membership table; nullable in Java (see `memberKindOf`). */
  memberKind?: MemberKind | null;
  /** SASATH-0382 — the address to show (e.g. the real email behind an Entra username). */
  displayEmail?: string | null;
  // Spec 0058 (saas-auth SASATH-0076) — the global `oidcSsoEnabled` boolean is
  // dropped (the `tenant_users.oidc_sso_enabled` column is removed). OIDC/SSO
  // access is now per-(member, client) — see `EffectiveMemberDto.accessState`
  // and the client-scoped `membersApi.setOidcAccess`.
}

/** SASATH-0376 — a member's kind; the role fallback only covers a null `memberKind`. */
export function memberKindOf(m: TenantMemberDto): MemberKind {
  return m.memberKind ?? (m.role === 'USER' || m.role === 'GUEST' ? 'CUSTOMER' : 'STAFF');
}

export interface AddMemberRequest {
  email: string;
  role: TenantUserRole;
}

// ---------------------------------------------------------------------------
// Tenant invitation (saas_auth spec 0011)
//
// Modeled on the shipped Java `dto/saasadmin/TenantInvitationDto.java` (the
// list + revoke response). The list item is keyed `id` (Long) — NOT
// `invitationId`; only the separate create response (`InvitationResponse`,
// modeled as `SendInvitationResponse`) carries `invitationId` + the one-time
// `inviteUrl`. The revoke path's `{invitationId}` value is this row's `id`.
// Timestamps are ISO-8601 strings (Java `LocalDateTime` createdAt / `Instant`
// expiresAt/acceptedAt/revokedAt).
// ---------------------------------------------------------------------------

export interface TenantInvitationDto {
  id: number;
  email: string;
  role: TenantUserRole;
  status: InvitationStatus;
  tenantId: number;
  invitedByUserId?: number | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string | null;
  revokedAt?: string | null;
  // Spec 0058 (saas-auth SASATH-0076) — replaces the dropped global
  // `oidcSsoEnabled` boolean: the explicit OIDC clients granted to the
  // membership created on accept. List of granted client DB ids (`clientDbId`);
  // `[]`/absent = no OIDC access. Client names are resolved client-side via the
  // picker (`tenantsApi.oidcClientsForTenant`). Read field name unconfirmed
  // against the pending SASATH-0076 wire — see the delivery's Architect notes.
  oidcClientIds?: string[];
  /** SASATH-0383 — an address on the tenant's domain hint: no email, activates on first sign-in. */
  preAuthorized?: boolean;
}

export interface SendInvitationRequest {
  email: string;
  role: TenantUserRole;
  // Spec 0058 (saas-auth SASATH-0076) — replaces the removed `oidcSsoEnabled`
  // boolean: the explicit OIDC clients to grant on accept (0/1/many). Optional;
  // `[]`/omit = the membership joins with no OIDC access. Values are client DB
  // ids (`clientDbId`) sourced from `tenantsApi.oidcClientsForTenant`.
  oidcClientIds?: string[];
}

/**
 * Shipped Java `InvitationResponse` (the 201 create body). `inviteUrl` carries
 * a one-time token and is intentionally NOT surfaced in the UI (spec 0008
 * Non-Goal); modeled here only so `invitationsApi.send` is typed accurately.
 */
export interface SendInvitationResponse {
  success: boolean;
  invitationId: number;
  email: string;
  tenantId: number;
  role: string;
  inviteUrl: string;
  expiresAt?: string | null;
  /** SASATH-0383 — absent before 0383 ships. */
  preAuthorized?: boolean;
  deliveryMode?: 'PRE_AUTHORIZED' | 'EMAILED';
}

// ---------------------------------------------------------------------------
// Email templates
// ---------------------------------------------------------------------------

export interface EmailTemplateDto {
  id: number;
  tenantSlug: string;
  templateName: EmailTemplateName;
  filename: string;
  createdAt: string;
}

export interface UpsertEmailTemplateRequest {
  templateName: EmailTemplateName;
  filename: string;
}

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------

/**
 * Login-template values accepted by saas-auth 0016.
 * Absent / null → saas_login treats as 'centered'.
 */
export type BrandTemplate = 'centered' | 'split-hero' | 'backdrop';

export const BRAND_TEMPLATES: readonly BrandTemplate[] = ['centered', 'split-hero', 'backdrop'];

export interface BrandDto {
  id: number;
  productId: number;
  slug: string;
  displayName: string;
  // Spec 0015 — editable key/base pairs + read-only composed URLs + global base
  logoImage?: string | null;
  logoBaseUrl?: string | null;
  logoUrl?: string | null;
  faviconImage?: string | null;
  faviconBaseUrl?: string | null;
  faviconUrl?: string | null;
  heroImage?: string | null;
  heroBaseUrl?: string | null;
  heroImageUrl?: string | null;
  globalAssetBaseUrl?: string | null;
  colors: Record<string, unknown>;
  fonts: Record<string, unknown>;
  copyStrings: Record<string, unknown>;
  authProviders: string[];
  signupEnabled: boolean;
  passwordResetEnabled: boolean;
  createdAt: string;
  updatedAt?: string;
  // Spec 0016 — top-level login-template + marketing fields (all optional/nullable)
  template?: BrandTemplate | null;
  shortName?: string | null;
  tagline?: string | null;
  heroCopy?: string | null;
  location?: string | null;
  // Spec 0030 — the brand-side guest-signup toggle (derived boolean). The URL +
  // label now live on the product (`guestSignupUrl`/`guestSignupLabel`).
  guestSignupEnabled?: boolean;
  primaryColorHover?: string | null;
}

export interface CreateBrandRequest {
  productId: number;
  slug: string;
  displayName: string;
  // Spec 0015 — editable key/base pairs (no read-only composed URLs in requests)
  logoImage?: string | null;
  logoBaseUrl?: string | null;
  faviconImage?: string | null;
  faviconBaseUrl?: string | null;
  heroImage?: string | null;
  heroBaseUrl?: string | null;
  colors?: Record<string, unknown>;
  fonts?: Record<string, unknown>;
  copyStrings?: Record<string, unknown>;
  authProviders?: string[];
  signupEnabled?: boolean;
  passwordResetEnabled?: boolean;
  // Spec 0016 — top-level (not nested into colors/copyStrings on the FE)
  template?: BrandTemplate | null;
  shortName?: string | null;
  tagline?: string | null;
  heroCopy?: string | null;
  location?: string | null;
  // Spec 0030 — nullable Boolean on the wire (defaults false). No URL on the brand.
  guestSignupEnabled?: boolean | null;
  primaryColorHover?: string | null;
}

export type UpdateBrandRequest = Omit<CreateBrandRequest, 'productId'>;

// ---------------------------------------------------------------------------
// Brand theme (SASADM-0154; saas-auth SASATH-0346 — handoff "go" §3.1 and the
// brand-theme contract §3/§4/§5). Colours are lowercase `#rrggbb`.
// ---------------------------------------------------------------------------

export type ThemeMode = 'light' | 'dark';
export type ThemeSurfaceStyle = 'light-tinted' | 'pure';
/** `custom` is read-only: GET may return it, a PUT never sends it. */
export type ThemeCornerStyle = 'sharp' | 'rounded' | 'pill' | 'custom';
export type ThemeFontSource = 'google' | 'system';

export interface ThemeSeeds {
  primary: string;
  /** `null` = derived (Auto). */
  accent: string | null;
  neutral: string | null;
  surfaceStyle: ThemeSurfaceStyle;
  success: string | null;
  warning: string | null;
  error: string | null;
  info: string | null;
  /** 0..8 slots; a missing or `null` slot uses the platform default. */
  categorical: (string | null)[];
  moneyPositive: string | null;
  moneyNegative: string | null;
}

export interface ThemeFont {
  family: string;
  source: ThemeFontSource;
}

export interface ThemeInput {
  seeds: ThemeSeeds;
  cornerStyle: ThemeCornerStyle;
  fonts: { body: ThemeFont; heading: ThemeFont; mono: ThemeFont };
  /** Per-mode role → hex pins; an absent role is derived. */
  overrides: { light: Record<string, string>; dark: Record<string, string> };
}

export interface ContrastFailure {
  mode: ThemeMode;
  /** A contract §4 role, or a categorical pair such as `categorical[3].ink`. */
  fg: string;
  bg: string;
  fgColor: string;
  bgColor: string;
  ratio: number;
  required: number;
}

export interface ThemeContrast {
  ok: boolean;
  failures: ContrastFailure[];
}

export interface ThemeWarning {
  code: string;
  message: string;
}

/** Contract §5 — only the parts the editor reads. */
export interface ResolvedThemeFont {
  family: string;
  /** Ready for CSS `font-family`. */
  stack: string;
  source?: string;
  /** A `fonts.googleapis.com` stylesheet for a google family; `null` otherwise. */
  cssUrl: string | null;
  weights: number[];
}

/** Contract §3 categorical entry. */
export interface ResolvedCategorical {
  fill: string;
  ink: string;
  soft: string;
  line: string;
}

/**
 * The contract §3 resolved document, typed only where the editor reads it.
 * `modes.<mode>` carries every contract §4 role (colour strings); everything
 * else stays `unknown`.
 */
export interface ResolvedTheme {
  // Every role is read as optional (noUncheckedIndexedAccess). SASADM-0172 (SASATH-0389, engine 6):
  // `appBarAccent` — always derived, never overridable — is absent from older documents; fall back
  // to `appBarInk`.
  modes: Record<ThemeMode, Record<string, string>>;
  categorical: Record<ThemeMode, ResolvedCategorical[]>;
  radius: { style: string; control: number; card: number; dialog: number };
  fonts: { body: ResolvedThemeFont; heading: ResolvedThemeFont; mono: ResolvedThemeFont };
  /** Contract §3 — absolute asset URLs (or null). */
  assets?: ResolvedAssets;
  [key: string]: unknown;
}

/**
 * Contract §3 `resolved.assets`. `logo` feeds the Theme preview app bar
 * (SASADM-0154); the rest are the SASADM-0156 image slots' current values.
 */
export interface ResolvedAssets {
  logo?: string | null;
  logoReversed?: string | null;
  mark?: string | null;
  favicon?: { svg?: string | null; png?: string | null };
  appleTouchIcon?: string | null;
  maskableIcon?: string | null;
  hero?: string | null;
  onboardingIllustration?: { light?: string | null; dark?: string | null };
  emailHeader?: string | null;
  socialImage?: string | null;
  [key: string]: unknown;
}

/** SASADM-0156 — the #962 §3.5 image slots (`POST` / `DELETE …/assets/{slot}`). */
export type BrandAssetSlot =
  | 'logoReversed'
  | 'mark'
  | 'faviconSvg'
  | 'appleTouchIcon'
  | 'maskableIcon'
  | 'onboardingLight'
  | 'onboardingDark'
  | 'emailHeader'
  | 'socialImage';

/** `POST …/assets/{slot}` 200 body. The URL is content-addressed. */
export interface BrandAssetUploadResponse {
  slot: BrandAssetSlot;
  url: string;
}

export interface ThemeResponse {
  input: ThemeInput;
  resolved: ResolvedTheme;
  contrast: ThemeContrast;
  warnings: ThemeWarning[];
  etag: string;
}

export interface ThemePreviewResponse {
  resolved: ResolvedTheme;
  contrast: ThemeContrast;
  warnings: ThemeWarning[];
}

/** One `GET /api/branding/fonts` entry — the platform's Google Fonts allowlist. */
export interface FontOption {
  family: string;
  category: string;
  weights: number[];
}

/** `PUT …/theme` input: `cornerStyle` is omitted while it is still `custom`. */
export type ThemeSaveInput = Omit<ThemeInput, 'cornerStyle'> & {
  cornerStyle?: Exclude<ThemeCornerStyle, 'custom'>;
};

// Brand theme bundle (SASADM-0159; saas-auth SASATH-0351 §3.3).

/**
 * One report error row (also `error.errors[]` on an apply's 400 `BUNDLE_INVALID`).
 * `file` is null for a whole-zip error; `path` is a JSON path, or null for an
 * asset or malformed JSON.
 */
export interface BundleError {
  file: string | null;
  path: string | null;
  code: string;
  message: string;
}

/** §3.3 `changes` — empty after any container error. */
export interface BundleChanges {
  /** Theme input paths that differ, e.g. `seeds.primary`, `overrides.dark.bg`. */
  theme: { changed: string[] };
  /** Default copy keys, and each locale's keys, that differ. */
  copy: { default: string[]; locales: Record<string, string[]> };
  /** Slot wire names by what the apply would do to them. */
  assets: { added: string[]; replaced: string[]; cleared: string[]; unchanged: string[] };
}

/** `POST …/theme/bundle?dryRun=true` 200 body (inside the ApiResponse wrapper). */
export interface BundleReport {
  /** True only with no errors, `etagMatches !== false` and no blocking contrast. */
  ok: boolean;
  /** Null for another brand's bundle (not compared). */
  etagMatches: boolean | null;
  changes: BundleChanges;
  /** `PUT …/theme`'s row shape. */
  contrast: ThemeContrast;
  /** e.g. `BUNDLE_FROM_OTHER_BRAND`, `BUNDLE_CHANGES_IDENTITY`. */
  warnings: ThemeWarning[];
  errors: BundleError[];
}

// Per-locale brand copy (SASADM-0155; saas-auth SASATH-0346 — handoff "go" §3.5).

/** `GET …/copy-locales` (and the `PUT` 200 body). */
export interface CopyLocalesResponse {
  /** The locale the default strings are in (`"en"`); never translatable here. */
  defaultLocale: string;
  /** The translatable keys, in display order. */
  keys: string[];
  /** key → the default-locale string (edited on the legacy brand form), or null. */
  default: Record<string, string | null>;
  /** locale (`es` / `fr` / `pt`) → key → translation; an untranslated key is absent. */
  locales: Record<string, Record<string, string>>;
  /** locale → the keys with a default but no translation. */
  missing: Record<string, string[]>;
}

/**
 * `PUT …/copy-locales` body. Keys not mentioned are kept; `null` (or blank)
 * clears a translation. Text is trimmed server-side, up to 2000 characters.
 */
export interface CopyLocalesUpdate {
  locales: Record<string, Record<string, string | null>>;
}

// ---------------------------------------------------------------------------
// Product
// ---------------------------------------------------------------------------

/**
 * Full product DTO — mirrors shipped saas-auth `ProductDto.java` (spec 0010).
 * The read DTO now reflects every persisted product field (additive over the
 * earlier slim shape). Field names/types match the Java exactly:
 * `defaultBrandId` is the only nullable field; the two day counts are primitive
 * ints.
 *
 * Spec 0062 (saas-auth spec 0039) — the dead `supportsGhlSso` flag was dropped
 * backend-side (GHL/OIDC eligibility is governed by the OIDC client's scope +
 * per-member grant, never a product flag), so it is removed from the DTO and
 * the create/update requests.
 */
// SASATH-0092 — a product's authentication posture. PLATFORM (default): tenants of
// this product authenticate via saas-auth (brand/OIDC/login all apply). EXTERNAL:
// the product's apps authenticate externally (Entra/Azure SSO) — saas-admin uses this
// only to adjust look-and-feel (SASADM-0067); it changes NO authentication behavior.
export const AUTH_MODES = ['PLATFORM', 'EXTERNAL'] as const;
export type AuthMode = (typeof AUTH_MODES)[number];

export const APP_TOPOLOGIES = ['COMPOSED', 'SELF_CONTAINED'] as const;
export type AppTopology = (typeof APP_TOPOLOGIES)[number];

export interface ProductDto {
  id: number;
  slug: string;
  displayName: string;
  jwtAudience: string;
  defaultMfaPolicy: MfaPolicy;
  defaultBrandId?: number | null;
  defaultMfaReverifyDays: number;
  defaultMfaTrustedDeviceDays: number;
  // Spec 0030 — guest-signup link: relative path (composed against each tenant's
  // primary APP host by the backend) + optional display label. Both nullable.
  guestSignupUrl?: string | null;
  guestSignupLabel?: string | null;
  // Spec 0063 (saas-auth SASATH-0042) — per-product opt-in for the guest
  // email-verification endpoint (POST /api/guest/email-verification/request).
  // Read DTO includes it (defaults false). Typed optional FE-side so adding it
  // does not churn unrelated ProductDto fixtures; always read with `?? false`.
  guestVerificationEnabled?: boolean;
  // Spec 0059 (saas-auth SASATH-0077) — lightweight classification/display string
  // (`NOT NULL`, seeded `Default`). Always present on the read DTO. Affects no
  // behavior (tokens/audiences/assignment/routing) — grouping/filtering only.
  productGroup: string;
  // SASATH-0092 — product auth posture (see AuthMode). Always present on the read DTO
  // once SASATH-0092 is deployed; typed optional here (like guestVerificationEnabled)
  // to avoid churning fixtures — always read with `?? 'PLATFORM'`.
  authMode?: AuthMode;
  // ProductDto — SASADM-0134 (saas-auth SASATH-0286). A product's packaging, independent of
  // authMode: COMPOSED (default) is UI+API as separate hosts; SELF_CONTAINED is one WAR behind
  // one host. Neither axis implies the other. Always present on the read DTO once SASATH-0286
  // ships; typed optional (like authMode) to avoid fixture churn — always read with `?? 'COMPOSED'`.
  appTopology?: AppTopology;
  // SASADM-0140 (saas-auth SASATH-0296) — the path this product's API is deployed under on the
  // app server; requests arrive at `/api` and are rewritten to it. `null` means the product has
  // no API to route. Never shown to end users.
  apiContextRoot?: string | null;
  // Spec 0119 (saas-auth SASATH-0263) — per-product opt-in requiring MFA for
  // product-administrator logins (admin.<product-slug>.<base> console only, not
  // tenant/customer logins). Read DTO includes it (defaults false). Typed optional
  // FE-side (like guestVerificationEnabled) to avoid churning fixtures — always
  // read with `?? false`.
  productAdminMfaRequired?: boolean;
  // SASADM-0152 (saas-auth SASATH-0337) — MFA policy for reseller users signing in to the
  // product's reseller console. Always present on the read DTO (server default REQUIRED); typed
  // optional (like productAdminMfaRequired) to avoid churning fixtures — always read with
  // `?? 'REQUIRED'`.
  resellerMfaPolicy?: MfaPolicy;
  // SASADM-0129 (saas-auth SASATH-0281) — the product's own base domain; every tenant of
  // the product lives under it. NULLABLE, NOT OPTIONAL: always present in the payload and
  // `null` when the product uses the platform default (which is every product today).
  baseDomain: string | null;
  // SASADM-0172 (saas-auth SASATH-0388 §2) — the product's backend may create tenants from its own
  // public signup, server to server. SUPER_ADMIN only. Typed optional; read with `?? false`.
  selfServiceTenantCreate?: boolean;
}

/**
 * Create a product — mirrors shipped `CreateProductRequest.java`.
 * `defaultBrandId` is intentionally absent: a brand references the product, so
 * the product is created with a null default brand and the default is set later
 * via PUT (resolves the product↔brand chicken-and-egg). `slug` + `jwtAudience`
 * are set here and then immutable.
 */
export interface CreateProductRequest {
  slug: string;
  displayName: string;
  jwtAudience: string;
  defaultMfaPolicy: MfaPolicy;
  defaultMfaReverifyDays: number;
  defaultMfaTrustedDeviceDays: number;
  // Spec 0030 — guest-signup link (relative path) + label; nullable.
  guestSignupUrl?: string | null;
  guestSignupLabel?: string | null;
  // Spec 0063 (saas-auth SASATH-0042) — optional; omit/null → server stores false.
  guestVerificationEnabled?: boolean;
  // Spec 0059 (saas-auth SASATH-0077) — optional on the request; blank/omit →
  // server stores `Default`.
  productGroup?: string;
  // SASATH-0092 — optional on the request; omit/null → server stores `PLATFORM`.
  authMode?: AuthMode;
  // CreateProductRequest — SASADM-0134 — optional; omit/null → server stores 'COMPOSED'.
  appTopology?: AppTopology;
  // SASADM-0140 — optional; null/omitted means no API route.
  apiContextRoot?: string | null;
  // Spec 0119 (saas-auth SASATH-0263) — optional; omit/null → server stores false.
  productAdminMfaRequired?: boolean;
  // SASADM-0152 — carried per the spec's §Scope but never sent: saas-auth's
  // CreateProductRequest has no resellerMfaPolicy today (SASATH-0337), so a new product
  // stores REQUIRED. The editor sets it on update only.
  resellerMfaPolicy?: MfaPolicy;
  // SASADM-0172 (SASATH-0388 §2) — optional; omit → server stores false.
  selfServiceTenantCreate?: boolean;
}

/**
 * Update a product — mirrors shipped `UpdateProductRequest.java`.
 * `slug` and `jwtAudience` are immutable, so they are intentionally absent.
 * `defaultBrandId` can be set (the brand must belong to THIS product) or
 * cleared with `null`.
 */
export interface UpdateProductRequest {
  displayName: string;
  defaultMfaPolicy: MfaPolicy;
  defaultMfaReverifyDays: number;
  defaultMfaTrustedDeviceDays: number;
  defaultBrandId?: number | null;
  // Spec 0030 — guest-signup link (relative path) + label; nullable.
  guestSignupUrl?: string | null;
  guestSignupLabel?: string | null;
  // Spec 0063 (saas-auth SASATH-0042) — FULL REPLACE, not a partial patch: the
  // server writes `Boolean.TRUE.equals(value)`, so omitting/sending null silently
  // turns the feature OFF. The editor ALWAYS sends the current boolean back
  // (see ProductEditor toUpdateRequest).
  guestVerificationEnabled?: boolean;
  // Spec 0059 (saas-auth SASATH-0077) — omit → unchanged (NOT reset to
  // `Default`). The editor submits the full object, so it sends the current/edited
  // value back.
  productGroup?: string;
  // SASATH-0092 — omit/null → leave unchanged. The editor submits the full object,
  // so it always sends the current/edited value back.
  authMode?: AuthMode;
  // UpdateProductRequest — SASADM-0134 — omit/null → leave unchanged, identical to authMode. The
  // editor always sends the current/edited value back (see ProductEditor toUpdateRequest) —
  // sending an unchanged value is a safe no-op, so no bespoke change-detection is needed for
  // this field.
  appTopology?: AppTopology;
  // SASADM-0140 — optional; null/omitted means no API route.
  apiContextRoot?: string | null;
  // Spec 0119 (saas-auth SASATH-0263) — FULL REPLACE, not a partial patch: the
  // server writes `Boolean.TRUE.equals(value)`, so omitting/sending null silently
  // turns the requirement OFF. The editor ALWAYS sends the current boolean back
  // (see ProductEditor toUpdateRequest).
  productAdminMfaRequired?: boolean;
  // SASADM-0152 (saas-auth SASATH-0337) — omit/null → leave unchanged. The editor always sends
  // the current/edited value back, like authMode.
  resellerMfaPolicy?: MfaPolicy;
  // SASADM-0129 — send the current/edited value back like the other full-replace fields.
  // An empty input submits `null`, never `''`. Changing it REHOMES every existing tenant.
  baseDomain?: string | null;
  // SASADM-0172 (SASATH-0388 §2) — the editor always sends the current/edited boolean back.
  selfServiceTenantCreate?: boolean;
}

// SASADM-0129 (saas-auth SASATH-0281) — one target hostname the rehome could not claim
// because another tenant already holds it.
export interface ProductRehomeCollision {
  tenantId: number;
  slug: string;
  hostname: string;
  reason?: string | null;
}

// SASADM-0129 — present ONLY when `baseDomain` changed. `collisions` non-empty means the
// whole operation aborted and NOTHING was written. `hostnamesLeftAlone` counts vanity and
// platform-level rows deliberately not rewritten — information, never a failure.
export interface ProductRehomeSummary {
  fromBaseDomain: string | null;
  toBaseDomain: string | null;
  tenantsAffected: number;
  hostnamesRewritten: number;
  hostnamesLeftAlone: number;
  collisions: ProductRehomeCollision[];
  dryRun: boolean;
}

// The update response: the product, plus the rehome block when the domain changed.
export type ProductUpdateResult = ProductDto & { rehome?: ProductRehomeSummary };

// ---------------------------------------------------------------------------
// Product app portfolio (spec 0031)
//
// Mirrors shipped saas-auth `dto/saasadmin/AppDto.java` (wire DTO is `AppDto`;
// named `ProductAppDto` here for clarity FE-side). The handoff contract listed
// `{ slug, displayName, docroot }`; the shipped DTO additionally carries
// `id`, `productId`, and `createdAt`. SASATH-0189 adds `isPrimary` for the
// product's apex-anchoring FRONTEND app. `docroot` is an ABSOLUTE path under the
// platform's `app.routing.docroot-base` (e.g. C:/caddy/htdocs/athena-timesheet).
// `slug` is renamed through its dedicated cascade-aware endpoint, so
// UpdateAppRequest omits it.
// ---------------------------------------------------------------------------

// Spec 0043 — an app's kind. FRONTEND (the default, served from a Caddy docroot).
// BACKEND and SELF_CONTAINED (SASATH-0092) both carry an `upstream` (no docroot):
// BACKEND is a product API proxied to an `upstream` host:port; SELF_CONTAINED binds
// an APP-type hostname (like FRONTEND) but is reverse-proxied to a self-contained
// Tomcat WAR `upstream` (like BACKEND). Mirrors shipped saas-auth `entity/AppKind`
// (saas-auth 0049, 0092).
export const APP_KINDS = ['FRONTEND', 'BACKEND', 'SELF_CONTAINED'] as const;
export type AppKind = (typeof APP_KINDS)[number];

// SASATH-0208 — an app's scope. TENANT (the default) provisions
// `<app-slug>.<tenant>.<base>` on every tenant of the product; PRODUCT provisions
// exactly one `admin.<product-slug>.<base>` host for the whole product (a
// product-admin console). PRODUCT requires `kind=FRONTEND` and can never be the
// product's primary app.
export const APP_SCOPES = ['TENANT', 'PRODUCT'] as const;
export type AppScope = (typeof APP_SCOPES)[number];

// SASATH-0234 — the operator-facing portfolio role. The server derives the
// app's kind and scope from this value. SASATH-0285 adds SELF_CONTAINED: a
// single deployment serving its own front end + back end (kind=SELF_CONTAINED,
// scope=TENANT), primary-eligible and exclusive — a product with one can have
// no other app. SASATH-0336 adds RESELLER_ADMIN: the product's reseller console
// (kind=FRONTEND, scope=PRODUCT), at most one per product, requires the PRODUCT_ADMIN
// app, never on a SELF_CONTAINED product. SASATH-0367 adds AFFILIATE: the app tenants' affiliates
// use (kind=FRONTEND, scope=TENANT, host affiliate.<tenant>.<base>), at most one per product and
// never primary; a non-primary PUBLIC app may be re-tagged to it, never the reverse.
export const APP_ROLES = [
  'PUBLIC',
  'TENANT_ADMIN',
  'PRODUCT_ADMIN',
  'SELF_CONTAINED',
  'API',
  'RESELLER_ADMIN',
  'AFFILIATE',
] as const;
export type AppRole = (typeof APP_ROLES)[number];

export interface ProductAppDto extends WithRoutingDeploy {
  id: number;
  productId: number;
  slug: string;
  displayName: string;
  role: AppRole;
  // Read-only, server-derived from `role`; never send these fields back.
  kind: AppKind;
  scope: AppScope;
  isPrimary: boolean;
  hasHostnames: boolean;
  // FRONTEND apps carry `docroot`; BACKEND apps carry `upstream` (no docroot).
  docroot?: string | null;
  upstream?: string | null;
  // SASADM-0172 (SASATH-0388 §3) — AFFILIATE only: tenants get the affiliate host only where an
  // operator enables it (Tenant → App builds). Absent/false = every tenant gets one.
  tenantOptIn?: boolean;
  // SASADM-0172 (SASATH-0385 §5) — FRONTEND apps: the server-normalized iframe sources; null = none.
  cspFrameSrc?: string | null;
  // SASADM-0173 (SASATH-0390 §2) — FRONTEND apps with a docroot: APP keeps the SPA fallback,
  // STATIC serves real files and a real 404. Absent = APP.
  siteType?: SiteType;
  // SASADM-0173 (SASATH-0390 §3) — FRONTEND apps: server-normalized script / connect sources.
  cspScriptSrc?: string | null;
  cspConnectSrc?: string | null;
  // SASADM-0174 (SASATH-0390 §3, amended 2026-10-13) — FRONTEND apps: form-action sources.
  cspFormAction?: string | null;
  // SASADM-0179 (SASATH-0397) — FRONTEND apps: style / font sources; always serialized (null = unset).
  cspStyleSrc?: string | null;
  cspFontSrc?: string | null;
  // SASADM-0178 (SASATH-0395 §2) — always `true` once 0395 is deployed: set-primary takes
  // `?dryRun=true`. Absent = a pre-0395 server, which would ignore `dryRun` and do the real call.
  setPrimaryDryRun?: boolean;
  createdAt: string;
}

// SASADM-0178 (SASATH-0395 §2) — the tenants' main addresses (`<tenant>.<base>`) a set-primary
// moves (dry run) or moved (real call). The lists are capped; the counts are exact.
export interface ApexPlanMoved {
  tenantId: number;
  tenantSlug: string;
  hostname: string;
  fromAppId: number | null;
  fromAppSlug: string | null;
}
/** `reason`: PINNED, PRIMARY_APP_DISABLED, SELF_CONTAINED_PRIMARY or CHANGED. */
export interface ApexPlanSkipped {
  tenantId: number;
  tenantSlug: string;
  hostname: string;
  appId: number | null;
  appSlug: string | null;
  reason: string;
}
/** The previous app's own address that couldn't be provisioned: NAME_TAKEN, or a provisioning rejection. */
export interface ApexPlanNotProvisioned {
  tenantId: number;
  hostname: string;
  reason: string;
}
export interface ApexPlan {
  moved: ApexPlanMoved[];
  skipped: ApexPlanSkipped[];
  previousAppHostNotProvisioned: ApexPlanNotProvisioned[];
  movedCount: number;
  skippedCount: number;
  truncated: boolean;
}
/** SASADM-0178 — set-primary's `data`: the app, its `routingDeploy` and (SASATH-0395) `apexPlan`. */
export interface SetPrimaryResult extends ProductAppDto {
  apexPlan?: ApexPlan;
}

// SASADM-0173 (SASATH-0390 §2).
export type SiteType = 'APP' | 'STATIC';

// SASADM-0176 (saas-auth SASATH-0393 §1) — a TENANT-scope app delete removes the app's SYSTEM
// hostnames and revokes pending invitations to it; a PRODUCT-scope delete carries only
// `routingDeploy`. Every field is optional (a pre-0393 TENANT-scope delete returns null data).
export interface ProductAppDeleteResult extends WithRoutingDeploy {
  deletedSystemHostnames?: number;
  revokedInvitations?: number;
}

// SASADM-0172 — `routingDeploy` is the worst per-tenant outcome; omitted when no tenant hostname moved.
export interface AppSlugRenameResponse extends WithRoutingDeploy {
  app: ProductAppDto;
  rename: {
    oldSlug: string;
    newSlug: string;
    hostsRenamed: number;
    tenantsAffected: number;
  };
}

// SASATH-0234 — create/update choose an operator-facing role. The server derives
// kind/scope; FRONTEND roles send `docroot`, while API sends `upstream`.
export interface CreateProductAppRequest {
  slug: string;
  role: AppRole;
  displayName: string;
  docroot?: string;
  upstream?: string;
  // SASADM-0172 — AFFILIATE only (any other role → 400 VALIDATION_FAILED); never sent otherwise.
  tenantOptIn?: boolean;
  // SASADM-0172 — FRONTEND apps; `null` clears.
  cspFrameSrc?: string | null;
  // SASADM-0173 (SASATH-0390 §2–§3) — FRONTEND apps only (any other app → 400 VALIDATION_FAILED).
  // Source fields: `null` clears.
  siteType?: SiteType;
  cspScriptSrc?: string | null;
  cspConnectSrc?: string | null;
  // SASADM-0174 (SASATH-0390 §3, amended 2026-10-13) — FRONTEND apps: form-action sources.
  cspFormAction?: string | null;
  // SASADM-0179 (SASATH-0397) — FRONTEND apps: style / font sources.
  cspStyleSrc?: string | null;
  cspFontSrc?: string | null;
}

export interface UpdateProductAppRequest {
  // Omit to retain the current role. The server accepts PUBLIC ↔ TENANT_ADMIN and
  // PUBLIC → AFFILIATE (non-primary; SASATH-0367).
  role?: AppRole;
  displayName: string;
  docroot?: string;
  upstream?: string;
  // SASADM-0172 — AFFILIATE only. Turning it off plans every tenant's affiliate host and deploys
  // (`routingDeploy` on the response).
  tenantOptIn?: boolean;
  // SASADM-0172 — FRONTEND apps; `null` clears.
  cspFrameSrc?: string | null;
  // SASADM-0173 (SASATH-0390 §2–§3) — FRONTEND apps only; a siteType change redeploys
  // (`routingDeploy` on the response). Source fields: `null` clears.
  siteType?: SiteType;
  cspScriptSrc?: string | null;
  cspConnectSrc?: string | null;
  // SASADM-0174 (SASATH-0390 §3, amended 2026-10-13) — FRONTEND apps: form-action sources.
  cspFormAction?: string | null;
  // SASADM-0179 (SASATH-0397) — FRONTEND apps: style / font sources.
  cspStyleSrc?: string | null;
  cspFontSrc?: string | null;
}

// ---------------------------------------------------------------------------
// Platform defaults (spec 0037)
//
// Read-only, config-sourced platform fallback values surfaced by
// `GET /api/saas-admin/platform-defaults` (saas-auth 0038). Display-only —
// screens render these as the fallback/placeholder authority. `loginHostname`
// may be omitted. Cached per session (changes only on backend redeploy).
// ---------------------------------------------------------------------------

export interface PlatformDefaultsDto {
  emailFromAddress: string;
  emailFromName: string;
  supportEmail: string;
  authHostname: string;
  loginHostname?: string | null;
  // Platform edge host(s)/IP(s) used for custom-domain CNAME verification.
  dnsExpectedTargets?: string[];
}

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

export interface UserSummaryDto {
  id: number;
  name?: string;
  email: string;
  imageUrl?: string | null;
  emailVerified: boolean;
  googleLinked: boolean;
  createdAt: string;
}

export interface UserTenantDto {
  tenantId: number;
  tenantName: string;
  tenantSlug: string;
  role: TenantUserRole;
  status: TenantUserStatus;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// OIDC client
//
// Aligned to shipped Java `OidcClientResponse.java` (saas-auth/.../oidc/dto).
// Spec 0061 (saas-auth SASATH-0078) — the client's access boundary moved from a
// per-TENANT allow-list to a per-PRODUCT allow-list:
//   - the embedded `allowedTenants` summary list + the `…/allowed-tenants`
//     add/remove/list endpoints are REMOVED (replaced by `allowed-products`);
//   - create carries `allowedProductIds: number[]` (empty = Platform-scoped =
//     all tenants; non-empty = Product-scoped = only those products' tenants);
//   - the read carries `accessScope` (PLATFORM when no products, PRODUCT
//     otherwise) + `allowedProductIds: number[]`.
// `loginTenantId` (the SASATH-0075 login-host axis) is INDEPENDENT and unchanged
// — access scope and login mode are separate axes; do not couple them.
// CONTRACT STATUS — built firmly to the SASATH-0078 documented contract (Ready
// for Coding, NOT yet shipped: saas-auth still exposes allowed-tenants). No
// dual-shape tolerance — see the spec-0061 delivery's Architect notes for the
// sequencing dependency.
// ---------------------------------------------------------------------------

export const OIDC_ACCESS_SCOPES = ['PLATFORM', 'PRODUCT'] as const;
export type OidcAccessScope = (typeof OIDC_ACCESS_SCOPES)[number];

export interface OidcClientResponse {
  clientDbId: string;
  clientId: string;
  clientName: string;
  brandHost?: string | null;
  loginTenantId?: number | null;
  loginHostname?: string | null;
  // SASATH-0075 (spec 0057) — platform-scoped read indicator (login surface =
  // platform login host, no single login tenant). PENDING ship; when absent the
  // edit screen derives the mode from `loginTenantId == null`.
  platformScoped?: boolean;
  // SASATH-0073 (spec 0057) — whether `brandHost` is the resolved platform
  // DEFAULT (true) vs an EXPLICIT override (false). PENDING ship; when absent the
  // edit screen falls back to `brandHost` presence. The edit screen MUST render a
  // default as empty + placeholder (never the resolved value), so a follow-default
  // client is not silently pinned on save.
  brandHostIsDefault?: boolean;
  integrationType: string;
  enabled: boolean;
  redirectUris: string[];
  postLogoutRedirectUris: string[];
  scopes: string[];
  clientIdIssuedAt: string;
  // Spec 0061 (SASATH-0078) — per-product access scope (replaces the embedded
  // `allowedTenants` list). `accessScope` is PLATFORM when `allowedProductIds`
  // is empty (all tenants), PRODUCT otherwise (only those products' tenants).
  // Independent of `loginTenantId` (the login-host axis).
  accessScope: OidcAccessScope;
  allowedProductIds: number[];
}

export interface OidcClientCreateResponse extends OidcClientResponse {
  clientSecret: string;
  secretShownOnce?: boolean;
}

export interface CreateOidcClientRequest {
  clientName: string;
  clientId: string;
  // SASATH-0073 (spec 0057) — optional brand host. Omitted/null → the client
  // follows its public-origin host (NULL on the wire). Send a value only to
  // override; never submit the platform-default placeholder as a value.
  brandHost?: string;
  // SASATH-0075 (spec 0057) — optional login tenant (login-HOST axis). Omitted/
  // null → the login surface is the platform login host; a value makes it that
  // tenant's host. INDEPENDENT of the access scope below (spec 0061) — login
  // mode and access scope are separate axes; do not couple them.
  loginTenantId?: number | null;
  integrationType: string;
  redirectUris: string[];
  postLogoutRedirectUris: string[];
  scopes: string[];
  // Spec 0061 (SASATH-0078) — per-product access scope (replaces
  // `allowedTenantIds`). Empty = Platform-scoped (all tenants); non-empty =
  // Product-scoped (only those products' tenants).
  allowedProductIds: number[];
}

export interface RotateSecretResponse {
  clientId: string;
  clientSecret: string;
}

// ---------------------------------------------------------------------------
// OIDC effective members (spec 0038; per-client model spec 0058 / SASATH-0076)
//
// `GET /api/saas-admin/oauth-clients/{clientDbId}/effective-members` (path
// UNCHANGED) — every membership of the client's allow-listed tenants, including
// ineligible rows. Ordered by tenant slug then email. 404 (unknown client /
// pre-ship) → feature-detect to null.
//
// Spec 0058 (SASATH-0076): the global `oidcSsoEnabled` boolean is REPLACED by a
// three-state per-(member, client) grant, `accessState`:
//   - GRANTED     — an active grant (`enabled=true`).
//   - DISABLED    — an explicit grant turned off (`enabled=false`).
//   - NOT_GRANTED — no grant row at all (NEW; distinct from DISABLED).
// `eligible` = `membershipActive && accessState === 'GRANTED'`. The toggle is the
// client-scoped `membersApi.setOidcAccess`. Field name/enum built to the pending
// SASATH-0076 hand-off (no dual-shape tolerance) — see the delivery's Architect
// notes for the sequencing dependency.
// ---------------------------------------------------------------------------

export const OIDC_ACCESS_STATES = ['GRANTED', 'DISABLED', 'NOT_GRANTED'] as const;
export type OidcAccessState = (typeof OIDC_ACCESS_STATES)[number];

export interface EffectiveMemberDto {
  tenantId: number;
  tenantSlug: string;
  tenantName: string;
  userId: number;
  email: string;
  role: TenantUserRole;
  membershipActive: boolean;
  // Three-state per-(member, client) grant (replaces global `oidcSsoEnabled`).
  accessState: OidcAccessState;
  eligible: boolean;
}

export interface EffectiveMembersResponse {
  clientDbId: string;
  members: EffectiveMemberDto[];
}

// Spec 0058 (SASATH-0076) — invitation OIDC-client picker. `GET
// /api/saas-admin/tenants/{tenantId}/oidc-clients` lists the OIDC clients whose
// allow-list includes the tenant (empty when none), used to populate the invite
// form's OIDC-client multi-select (U4). Modeled to the documented hand-off.
export interface OidcClientPickerDto {
  clientDbId: string;
  clientId: string;
  clientName: string;
}

// ---------------------------------------------------------------------------
// Product → OIDC-clients reverse view (spec 0062; saas-auth SASATH-0079)
//
// `GET /api/saas-admin/products/{productId}/oidc-clients` — the OIDC clients in
// scope for a product (1 product → N clients), each tagged with HOW it is in
// scope and whether it can be detached HERE:
//   - `scope` PRODUCT  → this product is explicitly on the client's allow-list;
//     removable here (`removableFromProduct:true`) via the SASADM-0061
//     `allowed-products` DELETE.
//   - `scope` PLATFORM → the client is platform-scoped (all products); it covers
//     this product globally, so it is read-only here (`removableFromProduct:false`).
//     To stop it covering a product the admin changes the CLIENT's access scope on
//     the client's page — a DELETE here returns 409 CLIENT_IS_PLATFORM_SCOPED.
// WIRE SHAPE — `GET …/oidc-clients` returns the client list as a BARE ARRAY in
// `data` (`ApiResponse<ProductOidcClientDto[]>`), consistent with every other
// admin list endpoint. SASADM-0065 corrected an earlier `{ clients: [] }` wrapper
// assumption that did not match the shipped SASATH-0079 endpoint and crashed the
// product page. `scope` reuses the `OidcAccessScope` union above.
export interface ProductOidcClientDto {
  clientDbId: string;
  clientId: string;
  clientName: string;
  scope: OidcAccessScope;
  removableFromProduct: boolean;
}

// ---------------------------------------------------------------------------
// Product agent clients + published scopes (SASADM-0149; saas-auth SASATH-0331)
//
// Distinct from Connected apps (OIDC): a public OAuth 2.1 + PKCE agent client
// (no secret) bound to one product and one login tenant. `authorizeUrl` /
// `tokenUrl` are API-derived from that tenant's primary AUTH host — render
// verbatim; never compose. There is no client delete (disable only). The
// product backend owns the scope catalog over S2S; saas-admin lists and
// (SUPER_ADMIN) deletes. An agent client's `scopes` must be a subset of the
// catalog.
// ---------------------------------------------------------------------------

export interface ProductScopeDto {
  scope: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgentClientResponse {
  clientDbId: number;
  clientId: string;
  clientName: string;
  productId: number;
  productSlug: string;
  loginTenantId: number;
  loginTenantSlug: string;
  authorizeUrl: string;
  tokenUrl: string;
  redirectUris: string[];
  scopes: string[];
  enabled: boolean;
  createdAt: string;
}

export interface CreateAgentClientRequest {
  clientId: string;
  clientName: string;
  loginTenantId: number;
  redirectUris: string[];
  scopes: string[];
}

// ---------------------------------------------------------------------------
// Product internal API keys (spec 0044; saas-auth SASATH-0052)
//
// Per-product database-backed internal API keys — the `X-Internal-Api-Key`
// credential a product backend presents to saas-auth's `/internal/**`
// endpoints. Managed only by platform SUPER_ADMIN (same gate as OIDC clients).
//
// CONTRACT STATUS — CONFIRMED against the SHIPPED saas-auth SASATH-0052 U3
// (merged to saas-auth `main`, final-unit commit dd784d2, PR #165). Verified
// directly from the shipped Java DTOs + `SaasAdminController`:
//   - All three endpoints (issue/list/revoke) return the standard
//     `ApiResponse<T>` envelope — like every other `api/admin/*` module — so the
//     client uses the repo-standard `request<ApiResponse<T>>()` + `unwrap()`
//     (NOT the tolerant parsing 0044 needed for an unconfirmed shape).
//   - issue (201) → `InternalKeyCreatedDto`: `{ rawKey, key: InternalKeyDto }` —
//     the raw secret (returned EXACTLY once) plus the metadata NESTED under
//     `key`. NOT a flat metadata+rawKey object (the 0044 assumption).
//   - list (200) → `List<InternalKeyDto>` (a bare array — not `{ keys: [...] }`).
//   - revoke (200) → `InternalKeyDto`.
//   - raw-key field name `rawKey` (confirmed — `InternalKeyCreatedDto.rawKey`).
//   - `status` literals ACTIVE | REVOKED (`ProductInternalKey.Status` enum).
//   - `InternalKeyDto` fields: id/productId/label?/keyPrefix/status/createdAt/
//     createdBy/revokedAt? (camelCase, confirmed from the Java DTO).
//   - timestamps are Java `LocalDateTime` → ISO-8601 local strings WITHOUT a
//     zone/offset (e.g. `2026-06-19T18:30:00`); `new Date(...)` parses these as
//     local time, so the component renders them directly.
// ---------------------------------------------------------------------------

export const INTERNAL_KEY_STATUSES = ['ACTIVE', 'REVOKED'] as const;
export type InternalKeyStatus = (typeof INTERNAL_KEY_STATUSES)[number];

export interface InternalKeyMetadata {
  id: number;
  productId: number;
  keyPrefix: string;
  label?: string | null;
  status: InternalKeyStatus;
  createdAt: string;
  createdBy: string;
  revokedAt?: string | null;
}

export interface IssueInternalKeyRequest {
  label?: string;
}

/**
 * The issue (create) response (`InternalKeyCreatedDto`): the one-time raw secret
 * PLUS the stored metadata nested under `key`. The raw key is returned exactly
 * once and never retrievable afterward — it is held only in transient component
 * state for the single display lifecycle and is never persisted, logged, or sent
 * to telemetry. The nesting (`{ rawKey, key }`) is the confirmed shipped shape
 * (see CONTRACT STATUS above) — not a flat metadata+rawKey object.
 */
export interface IssueInternalKeyResponse {
  rawKey: string;
  key: InternalKeyMetadata;
}

// ---------------------------------------------------------------------------
// GHL Marketplace app registration + installs (SASADM-0138; saas-auth
// SASATH-0154 / SASATH-0288). A THIRD, distinct GHL record — do not confuse
// with `GhlIntegrationDto` (tenant-scoped, #2) or the OIDC client (#1). This
// one is product-scoped: saas-auth as an OAuth *client* to GHL, holding
// per-location install tokens. Verified against the shipped
// `SaasAdminController` (:1524/1542/1575) and `GhlMarketplaceAppService`,
// Contract-SHA 13aadda.
//
// The client secret VALUE never crosses this boundary in either direction:
// this surface carries only the Key Vault NAME it lives under
// (`clientSecretVaultName`), plus `clientSecretSet` reporting whether it
// currently resolves. No install token value is ever returned either — there
// is deliberately no such field.
//
// 404 is a NORMAL response on both endpoints, not an error: on the
// registration endpoint it means "not configured yet"; on the installs
// endpoint (SASATH-0288, not yet shipped) it means the endpoint doesn't exist
// yet. See `ghlMarketplaceApp.ts`.
// ---------------------------------------------------------------------------

export interface GhlMarketplaceAppDto {
  id: number | null;
  productId: number | null;
  clientId: string | null;
  clientSecretVaultName: string | null;
  clientSecretSet: boolean;
  redirectUri: string | null;
  // Space-separated, NOT an array — unlike OidcClientDto.scopes. Do not normalize.
  scopes: string | null;
  status: string | null; // 'ACTIVE' | 'DISABLED' — read-only; not on the upsert request
  createdAt: string | null;
  updatedAt: string | null;
}

// Exactly four fields. `clientSecretVaultName` is optional to the SERVER but
// the console ALWAYS sends it: omitting or blanking it makes the backend
// overwrite the stored value with the derived convention
// (`GhlMarketplaceAppService.upsertApp`'s `StringUtils.hasText` fallback) —
// the "vault-name trap".
export interface UpsertGhlMarketplaceAppRequest {
  clientId: string;
  redirectUri: string;
  scopes: string;
  clientSecretVaultName?: string;
}

// SASATH-0288 — endpoint pending, returns 404 until it ships. NO token value
// is carried in any form; there is deliberately no such field.
export interface GhlMarketplaceInstallDto {
  id: number;
  userType: 'LOCATION' | 'COMPANY';
  companyId: string | null;
  locationId: string | null; // null for a COMPANY install
  status: 'ACTIVE' | 'REVOKED';
  expiresAt: string | null;
  scopes: string | null;
  tenantId: number | null; // BOTH null when no tenant claims the location
  tenantSlug: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ---------------------------------------------------------------------------
// Routing readiness + deploy (spec 0042; saas-auth 0046)
//
// `GET /api/saas-admin/tenants/{id}/routing-readiness` (read) gates the in-app
// "Deploy routing changes" button. `ready` is true iff every hostname is
// `dnsVerified` AND every APP hostname has an app assigned; the `blocking` rows
// (+ `reason`) populate the disabled-state "what's left" list. Feature-detected:
// absent until saas-auth 0046 ships, so `routingApi.routingReadiness` resolves to
// null (404/error) and the button stays inert.
//
// `POST /api/saas-admin/routing/deploy` (write) is PLATFORM-WIDE — the render
// regenerates ALL tenants' routing from the DB in one idempotent, serialized
// pass; the per-tenant readiness check only gates when it's sensible to click
// from this screen. `forceServiceRestart` is a downtime rollback tool and is
// intentionally NOT exposed in the basic button (always false/unsent). Spec 0064
// (saas-auth SASATH-0093): the deploy is now synchronous & in-app — `200` →
// result counts (`DeployRoutingResponse`); a bad render/reload returns `500`
// `CADDY_CONFIG_INVALID` / `CADDY_RELOAD_FAILED`.
// ---------------------------------------------------------------------------

export interface RoutingReadinessHostname {
  hostname: string;
  type: HostnameType;
  dnsVerified: boolean;
  appAssigned: boolean;
  blocking: boolean;
  reason?: string | null;
}

export interface RoutingReadiness {
  tenantId: number;
  ready: boolean;
  hostnames: RoutingReadinessHostname[];
}

export interface DeployRoutingRequest {
  // @deprecated Spec 0064 — ignored server-side since the in-app deploy (SASATH-0093).
  // NOT sent by the client (deployRouting posts no body).
  forceServiceRestart?: boolean;
}

// Spec 0064 (saas-auth SASATH-0093) — the deploy is now synchronous & in-app
// (renders + validates + reloads Caddy in one request), so the response carries
// result counts instead of a GitHub run ref/URL.
export interface DeployRoutingResponse extends WithRoutingDeploy {
  status: string; // "rendered"
  tenants: number;
  fragmentsWritten: number;
  filesRemoved: number;
  reloaded: boolean;
}

// Spec 0064 (saas-auth SASATH-0093) — per-tenant in-app deploy result. `file` is
// the written `tenant-<slug>.caddy`, or null when the tenant has zero routable
// hosts (its fragment is removed). `reloaded` = Caddy actually reloaded.
export interface TenantRoutingDeployResponse extends WithRoutingDeploy {
  tenantId: number;
  file: string | null;
  appHosts: string[];
  loginHosts: string[];
  authHosts: string[];
  apiHosts: string[];
  reloaded: boolean;
}

// ---------------------------------------------------------------------------
// SASADM-0118 — default-hostname backfill (saas-auth SASATH-0186, hardened by
// SASATH-0247). `POST /api/saas-admin/hostnames/backfill-defaults` is
// PLATFORM-WIDE, idempotent and additive: it provisions missing LOGIN/AUTH/
// BRAND/ASSET rows for every tenant, leaving existing hostnames untouched.
// `skippedTenants` is NOT a failure list — SASATH-0247 deliberately skips a
// tenant with no presence on the platform base domain (minting platform hosts
// for a fully white-labeled tenant would break its session cookie).
// `truncated` means the walk stopped at `nextTenantId` and tenants remain;
// `notConfigured` means the base domain is unset and the call did nothing.
// ---------------------------------------------------------------------------

export interface HostnameBackfillSkippedTenant {
  tenantId: number;
  slug: string;
  reason: string;
  message?: string | null;
}

export interface HostnameBackfillSummary {
  tenantsProcessed: number;
  tenantsSkipped: number;
  rowsCreated: number;
  rowsSkipped: number;
  notConfigured: boolean;
  truncated: boolean;
  nextTenantId: number | null;
  skippedTenants: HostnameBackfillSkippedTenant[];
}

// ---------------------------------------------------------------------------
// Tenant GHL integration (spec 0045; saas-auth SASATH-0053)
//
// saas-auth is the single authority for each tenant's GoHighLevel integration,
// stored 1:1 per tenant. saas-admin stores nothing — it manages the config over
// the SUPER_ADMIN-gated `/api/saas-admin/tenants/{tenantId}/ghl-integration`
// surface and renders only masked metadata.
//
// CONTRACT STATUS — CONFIRMED against the SHIPPED saas-auth SASATH-0053 (merged
// to saas-auth `main` #168, 2026-06-19). Verified directly from the shipped Java:
//   - `GhlIntegrationDto` (dto/saasadmin) — the masked admin read.
//   - `UpsertGhlIntegrationRequest` (dto/saasadmin) — the PUT body.
//   - `SaasAdminController` GET/PUT/DELETE — all wrapped in `ApiResponse<T>`.
// Confirmed shape (NOT a tolerant/parallel build — the strict ApiResponse
// envelope is used, like members.ts):
//   - Masked token: `tokenSet: boolean` + `tokenSuffix?: string | null` (a short
//     trailing fragment, never the secret). The full token is NEVER returned on
//     this surface.
//   - `privateIntegrationToken` is OPTIONAL on the upsert (nullable Java field).
//     The service sets the token field unconditionally from the request, so a
//     PUT that OMITS the field would NULL the stored token — therefore the UI
//     OMITS `privateIntegrationToken` from the body when the password field is
//     left blank on an existing integration (write-only, never resent).
//   - `active` is optional on the request (defaults to true server-side); the UI
//     always sends it explicitly from the toggle.
//   - 404 on GET/DELETE when the tenant or its integration is absent; 403 for a
//     non-SUPER_ADMIN on every verb.
// CONTRACT DEVIATION FROM SPEC: the spec/brief listed `tenantSlug` on the read
// DTO, but the shipped `GhlIntegrationDto` does NOT include it (fields: id,
// tenantId, locationId, tokenSet, tokenSuffix, active, createdAt, updatedAt).
// `tenantSlug` is therefore typed as optional and not relied on by the UI; the
// tenant slug is already available from the workspace loader's `tenant`.
// ---------------------------------------------------------------------------

export interface GhlIntegrationDto {
  id?: number;
  tenantId: number;
  // Not returned by the shipped SASATH-0053 admin read; kept optional only to
  // match the spec's field list. Do not rely on it — use the loader's tenant.
  tenantSlug?: string | null;
  locationId: string | null;
  // Masked token indicator — the full token is never returned here.
  tokenSet: boolean;
  tokenSuffix?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetGhlIntegrationRequest {
  locationId: string;
  // Write-only. Sent on create; on an existing integration sent only when the
  // admin types a replacement, and OMITTED entirely when left blank.
  privateIntegrationToken?: string;
  active: boolean;
}

// ---------------------------------------------------------------------------
// Captcha (SASATH-0149 config CRUD · SASATH-0151 Cloudflare automation ·
// SASATH-0152 per-tenant enforce) — spec SASADM-0086.
//
// saas-auth is the single authority for each tenant's Turnstile / Captcha
// config, stored 1:1 per tenant and managed over the SUPER_ADMIN-gated
// `/api/saas-admin/tenants/{tenantId}/captcha` surface. saas-admin stores
// nothing — it renders the config + widget state and drives the Cloudflare
// automation. Secret hygiene mirrors GHL exactly: the secret is write-only,
// never returned (only `secretKeySet` / `secretKeySuffix`), and a PUT NEVER clears it
// (the caller omits `secretKey` when blank; DELETE is the only clear path).
//
// CONTRACT STATUS — build-to-contract against SASATH-0149/0151/0152 (backend
// may not be live yet; MSW-mock in tests). Envelope assumption: every response
// is wrapped in `ApiResponse<T>`, matching ghlIntegration/resourceLimits/
// operators. If the wire ships un-enveloped or GET returns an empty default
// object instead of 404, those are small reconcile follow-ups, not blockers.
// ---------------------------------------------------------------------------

export interface CaptchaConfigDto {
  enabled: boolean;
  provider?: string; // not returned by GET …/captcha; optional
  siteKey: string | null; // public site key; null when unset
  secretKeySet: boolean; // a secret is stored (plaintext NEVER returned)
  secretKeySuffix: string | null; // masked suffix (e.g. "…F4bM"); null when unset
  enforce: boolean | null; // null = inherit global · true = enforce · false = observe
}

export interface SetCaptchaConfigRequest {
  enabled: boolean;
  siteKey: string;
  // Write-only. OMIT when blank (keep existing); non-blank → replace. Never sent
  // to clear — clearing the secret is DELETE-only.
  secretKey?: string;
  enforce: boolean | null; // null = inherit · true = enforce · false = observe
}

export interface CaptchaWidgetHostname {
  hostname: string;
  type: HostnameType; // APP | LOGIN (the widget holds APP+LOGIN hosts)
  registered: boolean;
}

export interface CaptchaWidgetDto {
  provisioned: boolean;
  siteKey?: string | null;
  mode?: string; // "managed"
  widgetDomains?: string[];
  hostnames?: CaptchaWidgetHostname[];
  widgetUnavailable?: boolean; // provisioned:true + Cloudflare read error
}

export interface ProvisionCaptchaResponse {
  alreadyProvisioned?: boolean;
}

// ---------------------------------------------------------------------------
// Resource-limit plans / entitlements (spec 0050; saas-auth SASATH-0062)
//
// saas-auth is the single authority for per-tenant resource limits, modeled as
// product-scoped **definitions** (levers: key/label/value_type/default_value),
// **plans** (named tiers), per-plan **limit values**, and a tenant→plan
// assignment. saas-admin is the SUPER_ADMIN CRUD consumer over the
// `/api/saas-admin/**` surface; it stores nothing and enforces nothing (the
// product backend reads/enforces via the s2s read, out of scope here).
//
// CONTRACT STATUS — ASSUMED, built firmly to the SINGLE documented SASATH-0062
// contract (no tolerant/dual-shape parsing). The shipped saas-auth SASATH-0062
// delivery (local repo, "Needs Review") covers only U1 (schema/entities/repos);
// the saas-admin CRUD controller + `dto/saasadmin/*` DTOs are NOT yet shipped,
// so the wire shapes below are taken from the SASATH-0062 + spec-0050 documented
// contract, not verified against shipped Java. If the shipped shape later
// differs, that is a follow-up reconcile spec — NOT re-introduced tolerance.
// Confirmed from the shipped SASATH-0062 U1 schema/delivery:
//   - `value_type` ∈ {INT, BOOL, STRING} stored as a STRING enum.
//   - lever values + a definition's default are STORED AS STRINGS interpreted
//     per `value_type` (gap #2 → strings); the typed coercion lives in the UI
//     (U2/U3/U4), so these DTOs carry the raw string values.
//   - field names follow the standard camelCase mapping of the snake_case
//     columns (`product_id`→`productId`, `value_type`→`valueType`,
//     `default_value`→`defaultValue`, `definition_id`→`definitionId`).
// All endpoints return the standard `ApiResponse<T>` envelope (every other
// `/api/saas-admin/**` consumer does), so this module uses the strict
// `request<ApiResponse<T>>()` + `unwrap()` pattern of internalKeys/ghlIntegration.
// ---------------------------------------------------------------------------

export const LIMIT_VALUE_TYPES = ['INT', 'BOOL', 'STRING'] as const;
export type LimitValueType = (typeof LIMIT_VALUE_TYPES)[number];

/**
 * A product-scoped limit **definition** (lever). `key` is the immutable lever
 * identity (set on create, never updated). `defaultValue` is a string (or null)
 * interpreted per `valueType` — the typed coercion lives in the UI.
 */
export interface LimitDefinition {
  id: number;
  productId: number;
  key: string;
  label: string;
  valueType: LimitValueType;
  defaultValue: string | null;
}

/** Create a definition. `key` + `valueType` are fixed at create. */
export interface CreateLimitDefinitionRequest {
  key: string;
  label: string;
  valueType: LimitValueType;
  defaultValue?: string | null;
}

/**
 * Update a definition — label/default only. `key` is immutable after create and
 * `valueType` is fixed, so both are intentionally absent from the request.
 */
export interface UpdateLimitDefinitionRequest {
  label: string;
  defaultValue?: string | null;
}

/**
 * A product-scoped **plan** (named entitlement tier).
 *
 * `limits` — the plan's **set** lever values, embedded on every plan read
 * (saas-auth SASATH-0064): the list, create, and update responses all carry the
 * array, so the plans table + per-plan editor render and pre-fill WITHOUT a
 * separate plan-values read or a definitions join. Each entry carries the lever's
 * display metadata inline (`key/label/valueType`) plus the plan's `value`. Holds
 * SET values only — a lever the plan hasn't set is absent (it falls back to the
 * definition `defaultValue`); a plan with no values → `[]` (never null). This
 * REPLACES spec-0050's assumed `limitValues: {definitionId, value}[]` (right idea,
 * richer shape + new name).
 *
 * CONTRACT NOTE (0051): the embedding is defined by saas-auth SASATH-0064
 * (Ready for Coding, NOT yet shipped — the shipped SASATH-0062 `PlanDto` is still
 * metadata-only). Built firmly to the revised hand-off (no tolerant/dual-shape
 * parsing); see the 0051 delivery's Architect notes for the cross-repo timing.
 */
export interface Plan {
  id: number;
  productId: number;
  name: string;
  description?: string | null;
  active: boolean;
  limits: PlanLimit[];
}

/**
 * One of a plan's SET lever values, embedded on the plan (SASATH-0064). Joins the
 * lever's **definition** metadata (`definitionId/key/label/valueType`) to the
 * plan's stored `value` (a string interpreted per `valueType`), so the table +
 * editor render without a definitions join. Richer than the lean `PlanLimitValue`
 * the dedicated `…/limits` GET/PUT returns, but carries NO per-value timestamps —
 * `plan_limit_values` has none (the revised hand-off resolves the spec's optional
 * `createdAt/updatedAt` to none).
 */
export interface PlanLimit {
  definitionId: number;
  key: string;
  label: string;
  valueType: LimitValueType;
  value: string;
}

export interface CreatePlanRequest {
  name: string;
  description?: string | null;
  active?: boolean;
}

export interface UpdatePlanRequest {
  name: string;
  description?: string | null;
  active?: boolean;
}

/**
 * One `{definition → value}` entry of the upsert REQUEST (`SetPlanLimitsRequest`).
 * `value` is a string interpreted per the definition's `valueType` (gap #2 →
 * strings). Addressed by `definitionId` (same-product definitions only — enforced
 * server-side). The confirmed upsert RESPONSE (the dedicated `PUT …/plans/{planId}/
 * limits`, left unchanged by SASATH-0064) returns the lean per-value set keyed the
 * same way (the wire also carries the lever `key`, not modeled here — unused).
 */
export interface PlanLimitValue {
  definitionId: number;
  value: string;
}

/**
 * Upsert request for a plan's limit values — the full `{definition → value}`
 * set. Each value is validated against its definition's `valueType` and must
 * reference a same-product definition (else the backend 400s).
 */
export interface SetPlanLimitsRequest {
  values: PlanLimitValue[];
}

/**
 * Assign a plan to a tenant. The plan must belong to the tenant's product, else
 * the backend 400s.
 */
export interface AssignTenantPlanRequest {
  planId: number;
}

/**
 * The tenant's current plan assignment — the dedicated read
 * `GET /api/saas-admin/tenants/{tenantId}/plan` → `{ planId, planName }`, or
 * `null` when the tenant has no plan (it then falls back to the definition
 * defaults). `planName` is used directly for display; `planId` to pre-select the
 * plan. This REPLACES spec-0050's assumed `TenantDto.planId` loader read.
 *
 * CONTRACT NOTE (0051): this dedicated GET is part of the revised SASATH-0062
 * hand-off but is NOT in the shipped saas-auth controller yet (only PUT/DELETE
 * `…/tenants/{tenantId}/plan` exist) and has no saas-auth spec yet — a cross-repo
 * follow-up. Built firmly to the revised hand-off; see the 0051 delivery.
 */
export interface TenantPlanAssignment {
  planId: number;
  planName: string;
}

// ---------------------------------------------------------------------------
// Tenant integration settings (pass-through) (spec 0052; saas-auth SASATH-0067)
//
// SASATH-0067 stores arbitrary per-tenant integration config as a generic
// group → key → value bag so a product backend can keep settings for any
// third-party integration without a bespoke table. Per tenant: a list of named
// **groups** (each with an `active` flag); within a group, string `key → value`
// settings; each key carries **`isSecret`**. SUPER_ADMIN-gated on
// `/api/saas-admin/**` under the standard `ApiResponse<T>` envelope. saas-admin
// manages opaque values; saas-auth never interprets them.
//
// SEPARATE from the native GHL integration (spec 0045 / `GhlIntegrationDto`):
// a backend MAY create a group named `GHL` in THIS store — that is a *different*
// thing from the native integration, which is unchanged.
//
// SECRET HYGIENE (mirrors the GHL token exactly): a key marked `isSecret` is
// masked on read — the full value is NEVER returned, only `valueSet` (whether a
// value is stored) + a short `valueSuffix` fragment. On upsert the secret's
// `value` is OMITTED unless the admin types a replacement (omit → the backend
// preserves the stored secret via SASATH-0066 merge-on-absent; non-blank →
// replaces; blank ≠ clear). Clearing a secret is the DELETE-key operation.
//
// CONTRACT STATUS — CONFIRMED against the SHIPPED saas-auth SASATH-0067 (merged
// to saas-auth `main`, PR #201, commit 6f12e54). spec-0052 was built to the
// SASATH-0067 *hand-off* field name `settings`, but the shipped Java wire names
// the read group's key array **`keys`** (`IntegrationSettingsGroupDto.keys`) and
// the upsert request array **`entries`** (`UpsertIntegrationSettingsRequest.entries`)
// — spec-0053 (this hotfix) aligns these DTOs firmly to the shipped wire (no
// dual-name tolerance). Verified directly from the shipped Java DTOs:
//   - read group's key array → `keys` (was `settings` in the hand-off).
//   - upsert request array → `entries` (was `settings`).
//   - secret discriminant → `isSecret` (UNCHANGED): the Java field is a wrapper
//     `Boolean isSecret` deliberately (not a primitive) so Lombok's getter is
//     `getIsSecret()` and Jackson serializes the property as `isSecret`, NOT the
//     `is`-stripped `secret`.
//   - masked secret fields → `valueSet` (boolean) + `valueSuffix` (short trailing
//     fragment, `SUFFIX_LENGTH = 4`, null when unset); non-secret `value` plain.
//     All UNCHANGED.
// ---------------------------------------------------------------------------

/**
 * One key within an integration-settings group, as returned by the masked admin
 * read. A discriminated union on `isSecret`:
 *   - non-secret → the plain `value` is returned;
 *   - secret → the value is masked: `valueSet` says whether a value is stored
 *     and `valueSuffix` is a short trailing fragment for identification (never
 *     the secret; `null` when nothing is stored). The full value is NEVER on
 *     this surface.
 */
export type IntegrationSetting =
  | { key: string; isSecret: false; value: string }
  | { key: string; isSecret: true; valueSet: boolean; valueSuffix: string | null };

/**
 * One integration-settings group for a tenant: its name, its group-level
 * `active` flag, and its keys (each masked per `isSecret`). The list endpoint
 * returns one of these per group; the single-group read returns just one. The
 * shipped SASATH-0067 wire names this array `keys` (confirmed — see CONTRACT
 * STATUS above); an empty group renders as `keys: []`.
 */
export interface IntegrationGroup {
  group: string;
  active: boolean;
  keys: IntegrationSetting[];
}

/**
 * Upsert body for a group (`PUT …/integration-settings/{group}`). Additive
 * merge: each listed entry is upserted; an unlisted key is left untouched
 * (removing a key is DELETE-key, removing the group is DELETE-group). `active`
 * is applied across the whole group. For a secret key, `value` is OMITTED to
 * preserve the stored secret (merge-on-absent) and supplied non-blank to replace
 * it; non-secret values are set as given. The shipped SASATH-0067 wire names
 * this array `entries` (confirmed — see CONTRACT STATUS above).
 */
export interface UpsertIntegrationGroupRequest {
  active: boolean;
  entries: { key: string; isSecret: boolean; value?: string }[];
}

/** SASADM-0164 (saas-auth SASATH-0379) — which auth host the tenant's Microsoft sign-in runs on. */
export type EntraRedirectHost = 'SYSTEM' | 'WHITELABEL';

export interface EntraRedirectUriDto {
  redirectHost: EntraRedirectHost;
  /** The URI for the current choice, or null. */
  redirectUri: string | null;
  /** Each choice's URI; WHITELABEL is null unless the tenant has a verified white-label auth host. */
  options: Record<EntraRedirectHost, string | null>;
}
